// 2.G.2 — Media-paneeli asset-detail-modaaliin.
//
// Eri layout per mediatyyppi:
//   - Foto: kamera + objektiivi + polttovali + ISO + aukko + suljinaika +
//     mitat
//   - Video: codec + resoluutio + FPS-arvio (duration / frame jos saatavilla),
//     kesto, audio-track
//   - Audio: codec + samplerate + bittisyvyys + kanavat + kesto
//   - Tuntematon: vain tekninen rivi (format + dimensiot)
// Encoded-application (esim. "Adobe Photoshop") nakyy aina jos asetettu.

import type { Asset } from "../api/types.js";

export type MediaPanelProps = {
  asset: Asset;
};

type Kind = "photo" | "video" | "audio" | "other";

function determineKind(asset: Asset): Kind {
  if (asset.videoCodec) return "video";
  if (asset.audioCodec && !asset.videoCodec && !(asset.width && asset.height)) {
    return "audio";
  }
  if (asset.width && asset.height) return "photo";
  return "other";
}

export function MediaPanel({ asset }: MediaPanelProps) {
  const kind = determineKind(asset);
  return (
    <div className="rounded-md border border-slate-800 bg-slate-900/40 p-4">
      <h4 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">
        {kindLabel(kind)}
      </h4>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm md:grid-cols-2">
        {kind === "photo" && <PhotoFields asset={asset} />}
        {kind === "video" && <VideoFields asset={asset} />}
        {kind === "audio" && <AudioFields asset={asset} />}
        {kind === "other" && <OtherFields asset={asset} />}
        {/* Yhteinen — kaikille jotka kayvat MediaInfo-pipelinin lapi */}
        {asset.encodedApplication && (
          <Row label="Tehnyt" value={asset.encodedApplication} />
        )}
        {asset.format && <Row label="Formaatti" value={asset.format} />}
      </dl>
    </div>
  );
}

function kindLabel(k: Kind): string {
  switch (k) {
    case "photo":
      return "Valokuva";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "other":
      return "Tiedosto";
  }
}

function PhotoFields({ asset }: { asset: Asset }) {
  return (
    <>
      {(asset.make || asset.model) && (
        <Row label="Kamera" value={[asset.make, asset.model].filter(Boolean).join(" ")} />
      )}
      {asset.cameraSerial && <Row label="Sarjannumero" value={asset.cameraSerial} />}
      {asset.lens && <Row label="Objektiivi" value={asset.lens} />}
      {asset.focalLength != null && (
        <Row label="Polttovali" value={`${asset.focalLength.toFixed(0)} mm`} />
      )}
      {asset.fNumber != null && <Row label="Aukko" value={`f/${asset.fNumber}`} />}
      {asset.iso != null && <Row label="ISO" value={String(asset.iso)} />}
      {asset.exposureTime != null && (
        <Row label="Suljinaika" value={formatExposureTime(asset.exposureTime)} />
      )}
      {asset.width && asset.height && (
        <Row label="Mitat" value={`${asset.width} × ${asset.height}`} />
      )}
      {asset.capturedAt && <Row label="Kuvattu" value={formatDate(asset.capturedAt)} />}
    </>
  );
}

function VideoFields({ asset }: { asset: Asset }) {
  return (
    <>
      {asset.videoCodec && <Row label="Video-codec" value={asset.videoCodec} />}
      {asset.width && asset.height && (
        <Row label="Resoluutio" value={`${asset.width} × ${asset.height}`} />
      )}
      {asset.duration != null && <Row label="Kesto" value={formatDuration(asset.duration)} />}
      {asset.audioCodec && <Row label="Audio-codec" value={asset.audioCodec} />}
      {asset.audioSampleRate != null && (
        <Row label="Audio-sample-rate" value={`${(asset.audioSampleRate / 1000).toFixed(1)} kHz`} />
      )}
      {asset.audioChannels != null && (
        <Row label="Audio-kanavat" value={String(asset.audioChannels)} />
      )}
      {(asset.make || asset.model) && (
        <Row label="Kamera" value={[asset.make, asset.model].filter(Boolean).join(" ")} />
      )}
      {asset.cameraSerial && <Row label="Sarjannumero" value={asset.cameraSerial} />}
      {asset.lens && <Row label="Objektiivi" value={asset.lens} />}
      {asset.capturedAt && <Row label="Kuvattu" value={formatDate(asset.capturedAt)} />}
    </>
  );
}

function AudioFields({ asset }: { asset: Asset }) {
  return (
    <>
      {asset.audioCodec && <Row label="Codec" value={asset.audioCodec} />}
      {asset.audioSampleRate != null && (
        <Row label="Sample-rate" value={`${(asset.audioSampleRate / 1000).toFixed(1)} kHz`} />
      )}
      {asset.audioChannels != null && (
        <Row label="Kanavat" value={String(asset.audioChannels)} />
      )}
      {asset.duration != null && <Row label="Kesto" value={formatDuration(asset.duration)} />}
      {asset.capturedAt && <Row label="Tallennettu" value={formatDate(asset.capturedAt)} />}
    </>
  );
}

function OtherFields({ asset }: { asset: Asset }) {
  return (
    <>
      {asset.width && asset.height && (
        <Row label="Mitat" value={`${asset.width} × ${asset.height}`} />
      )}
      {asset.duration != null && <Row label="Kesto" value={formatDuration(asset.duration)} />}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="break-all text-slate-200">{value}</dd>
    </>
  );
}

function formatExposureTime(t: number): string {
  if (t >= 1) return `${t.toFixed(1)} s`;
  // 1/X s muodossa
  const inv = 1 / t;
  return `1/${Math.round(inv)} s`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = (seconds - m * 60).toFixed(1);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("fi-FI", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
